import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  ShieldCheck,
  MapPin,
  BadgeCheck,
  HeartPulse,
  Phone,
} from "lucide-react";
import { BRAND, IMAGES, CORE_PILLARS } from "@/lib/constants";
import WaitlistForm from "@/components/WaitlistForm";

const iconMap = {
  shield: ShieldCheck,
  map: MapPin,
  badge: BadgeCheck,
  heart: HeartPulse,
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[#0A0B0E]" />
          <img
            src={IMAGES.heroAbstract}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover opacity-[0.28]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B0E]/60 via-[#0A0B0E]/75 to-[#0A0B0E]" />
          <div className="absolute inset-0 grid-noise opacity-40" />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 pt-20 md:pt-36 pb-16 md:pb-40">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="col-span-12 lg:col-span-7 min-w-0 animate-fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#28C0D8]/30 bg-[#28C0D8]/5 px-4 py-1.5 text-xs font-medium tracking-wide text-[#28C0D8]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#28C0D8] animate-pulse" />
                Opening soon in Bethesda, MD
              </div>
              <h1 className="mt-6 font-display text-[34px] sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-white break-words">
                A modern wellness
                <br />
                dispensary for{" "}
                <span className="text-[#28C0D8]">Bethesda.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-[#94A3B8] break-words">
                Lab-tested cannabis. Thoughtful guidance. Local delivery. MD
                Supply Wellness is bringing a premium, compliance-first
                dispensary experience to Maryland — built for patients and
                adult-use customers alike.
              </p>

              <div className="mt-8 md:mt-10 flex flex-col items-start sm:flex-row sm:items-center gap-3">
                <Link
                  to="/menu"
                  className="btn-teal-glow inline-flex items-center justify-center gap-2 rounded-full bg-[#28C0D8] px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC]"
                  data-testid="hero-shop-btn"
                >
                  Shop Coming Soon <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/careers#apply"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#28C0D8]/60 bg-[#28C0D8]/10 px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-semibold text-[#28C0D8] hover:bg-[#28C0D8]/20 transition"
                  data-testid="hero-apply-btn"
                >
                  Apply Now <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/[0.03] px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-medium text-white hover:bg-white/[0.06] transition"
                  data-testid="hero-contact-btn"
                >
                  Contact Us
                </Link>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/60">
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  className="inline-flex items-center gap-2 hover:text-white"
                >
                  <Phone className="h-4 w-4 text-[#28C0D8]" />
                  {BRAND.phone}
                </a>
                <span className="hidden sm:inline text-white/20">•</span>
                <span className="inline-flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-[#28C0D8]" />
                  {BRAND.address.line1}, {BRAND.address.line2}
                </span>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-5 min-w-0 animate-fade-up [animation-delay:120ms]">
              <div className="relative aspect-[16/11] sm:aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-30px_rgba(40,192,216,0.35)]">
                <img
                  src={IMAGES.macro1}
                  alt="Premium cannabis flower macro"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-transparent to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6">
                  <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8]">
                    Lab-tested · Maryland sourced
                  </p>
                  <p className="mt-2 font-display text-xl font-semibold text-white">
                    Flower, concentrates, edibles & more
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMING SOON STRIP */}
      <section
        className="relative border-t border-white/5 bg-[#0A0B0E]"
        data-testid="home-coming-soon"
      >
        <div
          className="absolute inset-x-0 -top-1 h-px bg-gradient-to-r from-transparent via-[#28C0D8]/40 to-transparent"
          aria-hidden="true"
        />
        <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-14 py-14 md:py-28 text-center">
          <img
            src={BRAND.logoTransparent}
            alt="MD Supply Wellness"
            className="mx-auto h-20 md:h-28 w-auto object-contain drop-shadow-[0_20px_60px_rgba(40,192,216,0.45)]"
            data-testid="home-coming-soon-logo"
          />

          <h2
            className="mt-6 md:mt-8 font-display text-[28px] leading-[1.1] sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white"
            data-testid="home-coming-soon-headline"
          >
            MD Supply Wellness is{" "}
            <span className="text-[#28C0D8]">coming soon.</span>
          </h2>

          <p className="mt-4 md:mt-5 mx-auto max-w-2xl text-[15px] md:text-[17px] leading-relaxed text-[#94A3B8]">
            We are putting the finishing touches on our new dispensary in{" "}
            <span className="text-white">Bethesda, Maryland</span>. Our goal is
            to officially open soon. Sign up now to be the first to know about
            our grand opening and job opportunities.
          </p>

          <div className="mt-7 md:mt-9 flex justify-center">
            <WaitlistForm source="home_coming_soon" />
          </div>

          <Link
            to="/careers#apply"
            className="mt-7 md:mt-9 inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#28C0D8] transition"
            data-testid="see-positions-link"
          >
            See future positions <ArrowDown className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* INTRO / ABOUT PREVIEW */}
      <section className="py-20 md:py-32 bg-[#0A0B0E] border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
            <div className="col-span-12 md:col-span-5">
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                About MD Supply Wellness
              </p>
              <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
                Wellness, without compromise.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-5 text-[17px] leading-relaxed text-[#94A3B8]">
              <p>
                We built MD Supply Wellness to give the Bethesda community a
                dispensary that feels as thoughtful as a clinic and as
                welcoming as your favorite local shop. Every product on our
                menu is sourced from Maryland-licensed cultivators and
                processors — and every interaction is grounded in education,
                compliance, and care.
              </p>
              <p>
                Whether you&apos;re a registered medical patient or a curious
                adult exploring cannabis wellness for the first time, our team
                will meet you where you are.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-[#28C0D8] font-medium hover:gap-3 transition-all"
                data-testid="home-learn-more"
              >
                Learn more about us <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CORE PILLARS */}
      <section className="py-20 md:py-32 bg-[#0A0B0E] border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                Why Choose Us
              </p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white max-w-2xl">
                Built on trust, quality, and Maryland compliance.
              </h2>
            </div>
            <p className="max-w-sm text-[15px] text-[#94A3B8]">
              Four pillars shape every product we stock and every conversation
              we have with you.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CORE_PILLARS.map((p, i) => {
              const Icon = iconMap[p.icon] || ShieldCheck;
              return (
                <div
                  key={p.title}
                  className="group rounded-2xl border border-white/10 bg-[#13161C] p-7 hover:border-[#28C0D8]/50 hover:-translate-y-1 transition-all duration-300"
                  style={{ animationDelay: `${i * 80}ms` }}
                  data-testid={`pillar-card-${i}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-[#28C0D8]/10 text-[#28C0D8] flex items-center justify-center mb-6 group-hover:bg-[#28C0D8] group-hover:text-[#0A0B0E] transition">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-white">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#94A3B8]">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CATEGORY TEASER */}
      <section className="py-20 md:py-32 bg-[#0A0B0E] border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-4 md:gap-8">
            <div className="col-span-12 md:col-span-6 lg:col-span-7 relative rounded-3xl overflow-hidden border border-white/10 min-h-[380px]">
              <img
                src={IMAGES.macro2}
                alt="Cannabis close-up"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0B0E] via-[#0A0B0E]/60 to-transparent" />
              <div className="relative p-8 md:p-12 h-full flex flex-col justify-end">
                <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                  Coming Soon
                </p>
                <h3 className="mt-4 font-display text-3xl md:text-4xl font-semibold tracking-tight text-white max-w-md">
                  Flower · Vapes · Edibles · Tinctures · Topicals
                </h3>
                <p className="mt-4 max-w-md text-[15px] text-[#CBD5E1]">
                  A carefully curated Maryland-compliant menu arriving in
                  Bethesda soon.
                </p>
                <div className="mt-6">
                  <Link
                    to="/menu"
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-2.5 text-sm text-white hover:bg-white/10"
                  >
                    See the menu preview <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-span-12 md:col-span-6 lg:col-span-5 flex flex-col gap-8">
              <div className="rounded-3xl border border-white/10 bg-[#13161C] p-8 md:p-10">
                <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                  Join the waitlist
                </p>
                <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                  Be the first to shop our launch.
                </h3>
                <p className="mt-3 text-sm text-[#94A3B8]">
                  Early access, exclusive opening-week offers, and community
                  event invites.
                </p>
                <div className="mt-6">
                  <WaitlistForm source="home" />
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-[#13161C] p-8 md:p-10">
                <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                  Find us
                </p>
                <h3 className="mt-3 font-display text-2xl font-semibold text-white">
                  {BRAND.address.line1}
                  <br />
                  {BRAND.address.line2}
                </h3>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a
                    href={`tel:${BRAND.phoneRaw}`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#28C0D8] px-4 py-2 text-sm font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC]"
                  >
                    <Phone className="h-4 w-4" /> {BRAND.phone}
                  </a>
                  <a
                    href={BRAND.mapsLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white hover:bg-white/5"
                  >
                    <MapPin className="h-4 w-4" /> Get directions
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NOW HIRING BAND */}
      <section
        className="border-t border-white/5 bg-[#0A0B0E]"
        data-testid="home-hiring-band"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-14 py-12 md:py-16">
          <div className="rounded-2xl border border-[#28C0D8]/30 bg-gradient-to-r from-[#28C0D8]/[0.06] to-transparent p-6 md:p-10">
            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                  Now Hiring · Bethesda
                </p>
                <h3 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                  Join our launch team.
                </h3>
                <p className="mt-2 text-[14px] md:text-[15px] text-[#94A3B8]">
                  Compliance Manager · AM Supervisor · PM Supervisor · Budtender
                </p>
              </div>
              <div className="flex flex-col items-start sm:flex-row sm:items-center gap-3">
                <Link
                  to="/careers#apply"
                  className="btn-teal-glow inline-flex items-center gap-2 rounded-full bg-[#28C0D8] px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC]"
                  data-testid="home-apply-btn"
                >
                  Apply Now <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/careers"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-medium text-white hover:bg-white/5"
                >
                  See positions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP PREVIEW */}
      <section className="bg-[#0A0B0E] border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                Our Location
              </p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight text-white">
                Rooted in the heart of Bethesda.
              </h2>
            </div>
            <Link
              to="/locations"
              className="inline-flex items-center gap-2 text-[#28C0D8] font-medium hover:gap-3 transition-all"
            >
              View location details <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="rounded-3xl overflow-hidden border border-white/10 h-[360px] md:h-[440px]">
            <iframe
              title="MD Supply Wellness map"
              src={BRAND.mapEmbed}
              width="100%"
              height="100%"
              style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.6)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[#0A0B0E]" />
          <img
            src={IMAGES.heroAbstract}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-[#0A0B0E]/70" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-32 text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white max-w-3xl mx-auto leading-[1.05]">
            Questions before we open?
            <br />
            <span className="text-[#28C0D8]">Let&apos;s talk.</span>
          </h2>
          <p className="mt-5 md:mt-6 text-[15px] md:text-[17px] text-[#94A3B8] max-w-xl mx-auto">
            Our team is already here in Bethesda, answering questions and
            preparing for launch.
          </p>
          <div className="mt-8 md:mt-10 flex flex-col items-center sm:flex-row gap-3 justify-center">
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="btn-teal-glow inline-flex items-center justify-center gap-2 rounded-full bg-[#28C0D8] px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC]"
            >
              <Phone className="h-4 w-4" /> Call {BRAND.phone}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-full border border-white/20 px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-medium text-white hover:bg-white/5"
            >
              Send a message
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
